import { v4 } from "uuid";
import { useSeti } from "../utils/useSeti.js";
const getPayloadConfig = ({ customerName, locale, startingChatMessagesPayload, viewType, webstats })=>{
    const dateToday = new Date().toString();
    const rguidSeed = v4();
    const standardConfig = {
        cguid: webstats?.clientGUID || '',
        cityName: locale?.city || '',
        countryCode: locale?.countryName || '',
        customerName,
        dateToday,
        isSessionActive: true,
        latitude: locale?.latitude || '',
        longitude: locale?.longitude || '',
        resolvedClientIP: webstats?.resolvedClientIP || '',
        stateCode: locale?.region || '',
        returnJSONResponse: true
    };
    const shouldEnableInlineWebSearchResultCitation = true;
    const shouldUseHotelImages = true;
    const shouldEnableRoomOptionsMarker = true;
    const shouldUseFlightListingMarker = true;
    const shouldUseRentalCarListingMarker = useSeti({
        testName: 'PENNY_RC_SEARCH',
        shouldFireImpression: false
    });
    const payloadConfig = {
        LANDING: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                productId: 5,
                rguid: `HOMEPAGE-${rguidSeed}`,
                ...standardConfig,
                ...startingChatMessagesPayload,
                hotelPayload: {
                    shouldUseSearchHotelsFeature: true,
                    isHotelSearchDateSelection: true,
                    isShowMoreListingCards: useSeti({
                        testName: 'CHATBOT_HOTEL_CARDS_LOAD_MORE',
                        shouldFireImpression: false
                    })
                },
                enabledActions: {
                    shouldEnableRoomOptionsMarker,
                    shouldUseHotelImages,
                    shouldEnableFeedbackIntent: true,
                    shouldEnableInlineLocationMetadata: true,
                    shouldEnableLLMBasedResult: true,
                    shouldEnableSelectedTripQuickActions: true,
                    shouldUseDynamicQuickActionsChips: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldEnableMarkdownTemplate: true,
                    isPennyAgentEnabled: true,
                    shouldUseFlightListingMarker,
                    shouldUseRentalCarListingMarker
                }
            }
        },
        PORTAL: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                isSessionActive: true,
                productId: -1,
                ...standardConfig,
                ...startingChatMessagesPayload,
                hotelPayload: {
                    shouldUseSearchHotelsFeature: true,
                    isHotelSearchDateSelection: true,
                    isShowMoreListingCards: useSeti({
                        testName: 'CHATBOT_HOTEL_CARDS_LOAD_MORE',
                        shouldFireImpression: false
                    })
                },
                enabledActions: {
                    shouldUseFlightListingMarker,
                    shouldEnableRoomOptionsMarker,
                    shouldUseHotelImages,
                    shouldEnableFeedbackIntent: true,
                    shouldEnableInlineLocationMetadata: true,
                    shouldEnableLLMBasedResult: true,
                    shouldEnableSelectedTripQuickActions: true,
                    shouldUseDynamicQuickActionsChips: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldEnableMarkdownTemplate: true,
                    isPennyAgentEnabled: true,
                    shouldUseRentalCarListingMarker
                },
                pennyPortalPayload: {
                    shouldUseSearchHotelsFeature: true
                }
            }
        },
        STAYDETAILS: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                productId: 5,
                ...startingChatMessagesPayload,
                hotelPayload: {
                    shouldUseSearchHotelsFeature: true,
                    isHotelSearchDateSelection: true,
                    ...startingChatMessagesPayload.hotelPayload
                },
                enabledActions: {
                    shouldEnableRoomOptionsMarker,
                    shouldEnableInlineLocationMetadata: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldEnableLLMBasedResult: true,
                    isPennyAgentEnabled: true,
                    shouldEnableMarkdownTemplate: true,
                    shouldUseHotelImages,
                    shouldUseRoomOptionsMarkerItem: true
                }
            }
        },
        STAYLISTINGS: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                productId: 5,
                ...startingChatMessagesPayload,
                hotelPayload: {
                    shouldUseSearchHotelsFeature: true,
                    isHotelSearchDateSelection: true,
                    ...startingChatMessagesPayload.hotelPayload
                },
                enabledActions: {
                    shouldEnableRoomOptionsMarker,
                    shouldUseHotelImages,
                    shouldEnableInlineLocationMetadata: true,
                    shouldUseDynamicQuickActionsChips: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldEnableLLMBasedResult: true,
                    shouldEnableMarkdownTemplate: true,
                    isPennyAgentEnabled: true,
                    shouldUseRoomOptionsMarkerItem: true
                }
            }
        },
        ITINERARY: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                isSessionActive: true,
                ...startingChatMessagesPayload,
                enabledActions: {
                    shouldEnableInlineLocationMetadata: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldEnableLLMBasedResult: true,
                    shouldEnableMarkdownTemplate: true,
                    isPennyAgentEnabled: true
                }
            }
        },
        DRIVE: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                ...startingChatMessagesPayload,
                carPayload: {
                    ...startingChatMessagesPayload.carPayload
                },
                enabledActions: {
                    isPennyAgentEnabled: true,
                    shouldEnableMarkdownTemplate: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldUseRentalCarListingMarker
                }
            }
        },
        CHECKOUT: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                ...startingChatMessagesPayload,
                hotelPayload: {
                    shouldUseChangeHotelDatesFeature: true,
                    shouldUseChangeHotelRoomsFeature: true,
                    shouldUseSwitchToPayLaterHotelFeature: true,
                    shouldDisplayChangeDatesBtn: true,
                    shouldUseSearchHotelsFeature: true,
                    shouldUseBookingFeature: true,
                    ...startingChatMessagesPayload.hotelPayload
                },
                flightPayload: {
                    shouldUseBookingFeature: true,
                    ...startingChatMessagesPayload.flightPayload
                },
                carPayload: {
                    shouldUseBookingFeature: true,
                    ...startingChatMessagesPayload.carPayload
                },
                packagePayload: {
                    shouldUseBookingFeature: true,
                    ...startingChatMessagesPayload.packagePayload
                },
                enabledActions: {
                    shouldEnableInlineLocationMetadata: true,
                    shouldEnableInlineWebSearchResultCitation,
                    shouldEnableLLMBasedResult: true,
                    isPennyAgentEnabled: true,
                    shouldEnableMarkdownTemplate: true
                }
            }
        },
        FLYDETAILS: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                ...startingChatMessagesPayload,
                flightPayload: {
                    ...startingChatMessagesPayload.flightPayload
                },
                enabledActions: {
                    shouldEnableInlineWebSearchResultCitation,
                    isPennyAgentEnabled: true,
                    shouldEnableMarkdownTemplate: true,
                    shouldUseFlightListingMarker
                }
            }
        },
        FLYLISTINGS: {
            isMobile: webstats?.isMobile,
            submitChatMessagesPayload: {
                ...standardConfig,
                ...startingChatMessagesPayload,
                flightPayload: {
                    ...startingChatMessagesPayload.flightPayload
                },
                enabledActions: {
                    shouldEnableInlineWebSearchResultCitation,
                    isPennyAgentEnabled: true,
                    shouldEnableMarkdownTemplate: true,
                    shouldUseFlightListingMarker
                }
            }
        }
    };
    const config = payloadConfig[viewType];
    if (!config) throw new Error(`No payload configuration found for viewType: ${viewType}. Add configuration to payloadConfig object in /react-components/penny-chat-bot/src/config/payloadConfig.ts. `);
    return config;
};
export { getPayloadConfig };
